import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { badgeClass, ConfirmDialog, dangerButtonClass, Field, FormActions, formatNumber, getApiErrorMessage, inputClass, inputErrorClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';
import { ProductChoice, ProductPicker } from '../quality/partApprovalShared';

type Tab = 'units' | 'products';
type Status = 'in_stock' | 'dispatched' | 'scrapped';

interface SerialRow {
  id: string;
  serialNumber: string;
  productId: string;
  productName: string;
  sku: string;
  status: Status;
  source: 'production' | 'purchase' | 'opening';
  workOrderNumber: string | null;
  batchNumber: string | null;
  sourceReference: string | null;
  challanNumber: string | null;
  salesOrderNumber: string | null;
  customerName: string | null;
  dispatchedOn: string | null;
  notes: string | null;
  createdAt: string;
}
interface Serial extends SerialRow {
  events: { id: string; kind: string; referenceNumber: string | null; note: string | null; createdAt: string; by: string | null }[];
}
interface SerialPage {
  data: SerialRow[];
  total: number;
  totalPages: number;
}
interface Summary {
  inStock: number;
  dispatched: number;
  scrapped: number;
  dispatchedThisMonth: number;
  serialisedProducts: number;
  mismatchedProducts: number;
}
interface SerialProduct {
  productId: string;
  name: string;
  sku: string;
  unit: string;
  serialPrefix: string | null;
  serialCounter: number;
  currentStock: number;
  inStock: number;
  dispatched: number;
  unnumbered: number;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<Status, { label: string; className: string }> = {
  in_stock: { label: 'In stock', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  dispatched: { label: 'Dispatched', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  scrapped: { label: 'Scrapped', className: 'bg-gray-50 text-gray-500 ring-gray-500/20' },
};
const SOURCE: Record<string, string> = { production: 'Made', purchase: 'Bought', opening: 'Opening stock' };
const EVENT: Record<string, string> = { made: 'Made on', received: 'Received on', registered: 'Registered as', dispatched: 'Dispatched on', returned: 'Back in stock from', scrapped: 'Scrapped' };
const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatMoment = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));
// Where a unit came from, in a few words
const origin = (row: SerialRow) => (row.source === 'production' ? row.workOrderNumber ?? 'A work order' : row.sourceReference || SOURCE[row.source]);

const serialApi = {
  page: async (params: object): Promise<SerialPage> => (await api.get('/serials', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/serials/summary')).data,
  one: async (id: string): Promise<Serial> => (await api.get(`/serials/${id}`)).data,
  products: async (search: string): Promise<SerialProduct[]> => (await api.get('/serials/products', { params: clean({ search }) })).data,
  saveSettings: async (productId: string, data: object) => (await api.put(`/serials/products/${productId}`, data)).data,
  register: async (data: object): Promise<{ registered: number; serialNumbers: string[] }> => (await api.post('/serials', data)).data,
  scrap: async (id: string, reason: string): Promise<Serial> => (await api.post(`/serials/${id}/scrap`, { reason })).data,
  remove: async (id: string): Promise<void> => {
    await api.delete(`/serials/${id}`);
  },
};

// ---------------------------------------------------------------- register units that were bought or already in stock

const RegisterForm: React.FC<{ products: SerialProduct[]; initialProductId: string; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ products, initialProductId, isPending, onCancel, onSubmit }) => {
  const [form, setForm] = useState({ productId: initialProductId, source: 'purchase', sourceReference: '', batchNumber: '', numbers: '', quantity: '', notes: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors({});
  };
  const product = products.find((candidate) => candidate.productId === form.productId);
  const listed = form.numbers.split(/[\n,;]+/).map((number) => number.trim()).filter(Boolean);
  const room = product ? Math.floor(product.unnumbered) : 0;
  const count = listed.length > 0 ? listed.length : Number(form.quantity) || 0;
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.productId) next.productId = 'Choose the product';
    if (listed.length === 0 && (!Number.isInteger(Number(form.quantity)) || Number(form.quantity) < 1)) next.numbers = 'List the serial numbers, or say how many to give out';
    else if (new Set(listed).size !== listed.length) next.numbers = 'A serial number is listed twice';
    else if (product && count > room) next.numbers = room > 0 ? `Only ${formatNumber(room)} unit(s) of the stock have no number yet` : 'Every unit in stock already has a number. Receive the goods first.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ productId: form.productId, source: form.source, sourceReference: form.sourceReference.trim() || null, batchNumber: form.batchNumber.trim() || null, notes: form.notes.trim() || null, ...(listed.length > 0 ? { serialNumbers: listed } : { quantity: Number(form.quantity) }) });
  };
  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Product" htmlFor="sr-product" required error={errors.productId} hint={product ? `${formatNumber(product.currentStock)} in stock, ${formatNumber(product.inStock)} with a number, ${formatNumber(Math.max(0, room))} without` : products.length === 0 ? 'No product is kept by serial number yet; see the Serialised products tab' : undefined}>
          <select id="sr-product" value={form.productId} onChange={set('productId')} className={cn(inputClass, errors.productId && inputErrorClass)}>
            <option value="">Choose…</option>
            {products.map((candidate) => (<option key={candidate.productId} value={candidate.productId}>{candidate.name} · {candidate.sku}</option>))}
          </select>
        </Field>
        <Field label="The units were" htmlFor="sr-source" required>
          <select id="sr-source" value={form.source} onChange={set('source')} className={inputClass}>
            <option value="purchase">Bought</option>
            <option value="opening">Already in stock before serial numbers were kept</option>
          </select>
        </Field>
        <Field label="Document they came on" htmlFor="sr-reference"><input id="sr-reference" type="text" maxLength={100} value={form.sourceReference} onChange={set('sourceReference')} placeholder="Goods receipt or vendor invoice" className={inputClass} /></Field>
        <Field label="Batch" htmlFor="sr-batch"><input id="sr-batch" type="text" maxLength={50} value={form.batchNumber} onChange={set('batchNumber')} className={inputClass} /></Field>
        <Field label="Serial numbers, as they are on the units" htmlFor="sr-numbers" error={errors.numbers} hint={listed.length > 0 ? `${formatNumber(listed.length)} number(s)` : 'One to a line, or separated by commas'} className="sm:col-span-2">
          <textarea id="sr-numbers" rows={4} value={form.numbers} onChange={set('numbers')} className={cn(inputClass, 'font-mono text-xs', errors.numbers && inputErrorClass)} />
        </Field>
        <Field label="Or give out this many numbers" htmlFor="sr-quantity" hint={product ? `They start with ${product.serialPrefix || product.sku}-` : undefined}>
          <input id="sr-quantity" type="number" min="1" step="1" disabled={listed.length > 0} value={listed.length > 0 ? '' : form.quantity} onChange={set('quantity')} className={cn(inputClass, 'text-right tabular-nums')} />
        </Field>
        <Field label="Notes" htmlFor="sr-notes"><input id="sr-notes" type="text" maxLength={300} value={form.notes} onChange={set('notes')} className={inputClass} /></Field>
      </div>
      <p className="text-xs text-gray-500">Units that are made get their numbers when their work order is completed; they are not registered here.</p>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : count > 0 ? `Register ${formatNumber(count)} Unit(s)` : 'Register Units'}</button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one unit and what happened to it

const SerialPanel: React.FC<{ serialId: string; canEdit: boolean; canDelete: boolean; onClose: () => void; onChanged: () => void }> = ({ serialId, canEdit, canDelete, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: serial, isLoading, isError, error } = useQuery({ queryKey: ['serials', 'one', serialId], queryFn: () => serialApi.one(serialId) });
  const [scrapping, setScrapping] = useState(false);
  const [reason, setReason] = useState('');
  const fail = (actionError: unknown) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 7000 });
  const scrap = useMutation({
    mutationFn: () => serialApi.scrap(serialId, reason.trim()),
    onSuccess: (saved) => {
      toast.success(`${saved.serialNumber} marked as scrapped`);
      queryClient.setQueryData(['serials', 'one', serialId], saved);
      setScrapping(false);
      onChanged();
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => serialApi.remove(serialId),
    onSuccess: () => {
      toast.success('Serial number taken back');
      onChanged();
      onClose();
    },
    onError: fail,
  });
  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !serial) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The unit could not be loaded.')}</p>;
  const facts = [
    { label: 'Came from', value: `${SOURCE[serial.source]}${serial.source === 'production' ? '' : serial.sourceReference ? ` · ${serial.sourceReference}` : ''}` },
    { label: 'Work order', value: serial.workOrderNumber ?? '—' },
    { label: 'Batch', value: serial.batchNumber ?? '—' },
    { label: 'In the system since', value: formatDate(serial.createdAt) },
    { label: 'Customer', value: serial.customerName ?? '—' },
    { label: 'Sales order', value: serial.salesOrderNumber ?? '—' },
    { label: 'Delivery challan', value: serial.challanNumber ?? '—' },
    { label: 'Dispatched on', value: formatDate(serial.dispatchedOn) },
  ];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, STATUS[serial.status].className)}>{STATUS[serial.status].label}</span>
        <span className="font-semibold text-gray-900">{serial.productName} <span className="font-normal text-gray-500">{serial.sku}</span></span>
        {serial.notes && <span className="text-gray-500">{serial.notes}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 text-sm ring-1 ring-gray-200 sm:grid-cols-4">
        {facts.map((fact) => (<div key={fact.label}><p className="text-xs uppercase tracking-wide text-gray-500">{fact.label}</p><p className="font-medium text-gray-900">{fact.value}</p></div>))}
      </div>
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">What happened to it</h4>
        <ol className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
          {serial.events.map((event) => (
            <li key={event.id} className="flex flex-wrap items-baseline gap-x-4 px-4 py-2">
              <span className="font-medium text-gray-900">{EVENT[event.kind] ?? event.kind}{event.referenceNumber ? ` ${event.referenceNumber}` : ''}</span>
              <span className="min-w-0 flex-1 text-gray-700">{event.note ?? ''}</span>
              <span className="text-xs text-gray-500">{formatMoment(event.createdAt)}{event.by ? ` · ${event.by}` : ''}</span>
            </li>
          ))}
        </ol>
      </section>
      {scrapping ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Field label="Why is the unit scrapped?" htmlFor="su-reason" required hint="This marks the unit. Take the stock itself out with a scrap note.">
            <input id="su-reason" type="text" maxLength={300} autoFocus value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          </Field>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setScrapping(false)} className={secondaryButtonClass}>Back</button>
            <button type="button" disabled={scrap.isPending || reason.trim().length < 3} onClick={() => scrap.mutate()} className={dangerButtonClass}>Confirm Scrapping</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
          {canDelete && serial.source !== 'production' && serial.status === 'in_stock' && serial.events.length <= 1 && (
            <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending} className={secondaryButtonClass} title="For a number registered by mistake">Take Back</button>
          )}
          {canEdit && serial.status === 'in_stock' && <button type="button" onClick={() => { setReason(''); setScrapping(true); }} className={secondaryButtonClass}>Scrap</button>}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- which products are kept by serial number

const ProductsPanel: React.FC<{ canEdit: boolean; onRegister: (productId: string) => void; onChanged: () => void }> = ({ canEdit, onRegister, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: products = [], isLoading } = useQuery({ queryKey: ['serials', 'products', ''], queryFn: () => serialApi.products('') });
  const [adding, setAdding] = useState<ProductChoice | null>(null);
  const [prefix, setPrefix] = useState('');
  const [switchingOff, setSwitchingOff] = useState<SerialProduct | null>(null);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['serials', 'products'] });
    onChanged();
  };
  const save = useMutation({
    mutationFn: ({ productId, isSerialised, serialPrefix }: { productId: string; isSerialised: boolean; serialPrefix: string | null; name: string }) => serialApi.saveSettings(productId, { isSerialised, serialPrefix }),
    onSuccess: (_saved, { isSerialised, name }) => {
      toast.success(isSerialised ? `${name} is now kept by serial number` : `${name} is no longer kept by serial number`);
      setAdding(null);
      setPrefix('');
      setSwitchingOff(null);
      refresh();
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'That could not be saved')),
  });
  const prefixValid = prefix.trim() === '' || /^[A-Za-z0-9][A-Za-z0-9/-]{0,19}$/.test(prefix.trim());
  return (
    <div className="space-y-4 p-4">
      {canEdit && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg bg-gray-50 p-3 ring-1 ring-gray-200">
          <div className="min-w-[16rem] flex-1">
            <Field label="Keep a product by serial number" htmlFor="sp-product">
              <ProductPicker id="sp-product" value={adding} onChange={setAdding} />
            </Field>
          </div>
          <Field label="Numbers start with" htmlFor="sp-prefix" hint="The SKU when left empty" error={prefixValid ? undefined : 'Up to 20 letters, digits, hyphens or slashes'}>
            <input id="sp-prefix" type="text" maxLength={20} value={prefix} onChange={(event) => setPrefix(event.target.value.toUpperCase())} placeholder={adding?.sku ?? 'GBX'} className={cn(inputClass, 'w-40', !prefixValid && inputErrorClass)} />
          </Field>
          <button type="button" disabled={!adding || !prefixValid || save.isPending} onClick={() => adding && save.mutate({ productId: adding.id, isSerialised: true, serialPrefix: prefix.trim() || null, name: adding.name })} className={primaryButtonClass}>
            Switch On
          </button>
        </div>
      )}
      <p className="text-xs text-gray-500">A unit of such a product gets its number when its work order is completed, and the units that leave are named on the delivery challan. Stock that has no number yet has to be registered before it can be dispatched.</p>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Product</th>
              <th scope="col" className={thClass}>Numbers start with</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Stock</th>
              <th scope="col" className={cn(thClass, 'text-right')}>With a number</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Without</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Dispatched</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <p className="text-sm font-medium text-gray-900">No product is kept by serial number</p>
                  <p className="mt-1 text-sm text-gray-500">Switch it on for products whose units have to be followed one by one.</p>
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr key={product.productId}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{product.name}<span className="block text-xs font-normal text-gray-500">{product.sku}</span></td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{product.serialPrefix || product.sku}-<span className="block text-xs text-gray-500">{formatNumber(product.serialCounter)} given out</span></td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(product.currentStock)} {product.unit}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(product.inStock)}</td>
                  <td className={cn('whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums', product.unnumbered !== 0 ? 'font-medium text-red-600' : 'text-gray-400')}>{product.unnumbered === 0 ? '—' : formatNumber(product.unnumbered)}{product.unnumbered < 0 && <span className="block text-xs">more numbers than stock</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(product.dispatched)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    {canEdit && product.unnumbered >= 1 && (
                      <button type="button" onClick={() => onRegister(product.productId)} className="mr-1 rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Register units of ${product.name}`}>Register</button>
                    )}
                    {canEdit && (
                      <button type="button" onClick={() => setSwitchingOff(product)} disabled={save.isPending} className="rounded-md px-2.5 py-1 text-xs font-semibold text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50" aria-label={`Stop keeping ${product.name} by serial number`}>Switch Off</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        isOpen={!!switchingOff}
        onClose={() => setSwitchingOff(null)}
        onConfirm={() => switchingOff && save.mutate({ productId: switchingOff.productId, isSerialised: false, serialPrefix: switchingOff.serialPrefix, name: switchingOff.name })}
        title={switchingOff ? `Stop keeping ${switchingOff.name} by serial number?` : ''}
        message={
          switchingOff
            ? `Units will no longer be asked for by number on challans, and new ones get no number when made.${switchingOff.inStock > 0 ? ` The ${formatNumber(switchingOff.inStock)} unit(s) in stock keep their numbers, but will no longer be followed when they leave.` : ''} Their history stays.`
            : ''
        }
        confirmLabel="Switch Off"
        pendingLabel="Switching off…"
        isPending={save.isPending}
      />
    </div>
  );
};

// ---------------------------------------------------------------- the page

const Serials: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('inventory.create');
  const canEdit = can('inventory.update');
  const canDelete = can('inventory.delete');
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<Tab>('units');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<Status | ''>('');
  const [registerFor, setRegisterFor] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['serials', 'page', { page, limit, search, status }],
    queryFn: () => serialApi.page({ page, limit, search, status }),
    placeholderData: keepPreviousData,
    enabled: tab === 'units',
  });
  const { data: summary } = useQuery({ queryKey: ['serials', 'summary'], queryFn: serialApi.summary });
  const { data: products = [] } = useQuery({ queryKey: ['serials', 'products', ''], queryFn: () => serialApi.products('') });
  const { data: viewing } = useQuery({ queryKey: ['serials', 'one', viewingId], queryFn: () => serialApi.one(viewingId as string), enabled: !!viewingId });

  const refresh = () => ['page', 'summary', 'products', 'available'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['serials', key] }));
  const register = useMutation({
    mutationFn: (input: object) => serialApi.register(input),
    onSuccess: (result) => {
      toast.success(`${formatNumber(result.registered)} unit(s) registered: ${result.serialNumbers.slice(0, 3).join(', ')}${result.registered > 3 ? ', …' : ''}`, { duration: 6000 });
      refresh();
      setRegisterFor(null);
      setTab('units');
    },
    onError: (registerError) => toast.error(getApiErrorMessage(registerError, 'The units could not be registered'), { duration: 8000 }),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const stats = [
    { label: 'Units in stock', value: formatNumber(summary?.inStock ?? 0), hint: `Of ${formatNumber(summary?.serialisedProducts ?? 0)} product(s) kept by serial number` },
    { label: 'Dispatched', value: formatNumber(summary?.dispatched ?? 0), hint: `${formatNumber(summary?.dispatchedThisMonth ?? 0)} this month` },
    { label: 'Scrapped', value: formatNumber(summary?.scrapped ?? 0), hint: 'Units marked as scrapped' },
    { label: 'Stock and numbers disagree', value: formatNumber(summary?.mismatchedProducts ?? 0), hint: 'Products with stock that has no number, or the other way round', alert: (summary?.mismatchedProducts ?? 0) > 0 },
  ];
  const tabs: { key: Tab; label: string }[] = [
    { key: 'units', label: 'Units' },
    { key: 'products', label: 'Serialised products' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Serial Numbers</h1>
          <p className="mt-1 text-sm text-gray-500">Products that are followed unit by unit: which work order made each unit, and which customer it went to on which challan.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setRegisterFor('')} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Register Units
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.alert ? 'text-red-600' : 'text-gray-900')}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-4 pt-3" role="tablist">
          {tabs.map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => setTab(item.key)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>{item.label}</button>
          ))}
        </div>
        {tab === 'products' ? (
          <ProductsPanel canEdit={canEdit} onRegister={(productId) => setRegisterFor(productId)} onChanged={refresh} />
        ) : (
          <>
            <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by serial number, product, batch, work order, challan or customer" aria-label="Search serial numbers" className={cn(inputClass, 'pl-9')} />
              </div>
              <select value={status} onChange={(event) => { setStatus(event.target.value as Status | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-44')}>
                <option value="">All units</option>
                {(Object.keys(STATUS) as Status[]).map((value) => (<option key={value} value={value}>{STATUS[value].label}</option>))}
              </select>
            </div>
            <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className={thClass}>Serial number</th>
                    <th scope="col" className={thClass}>Product</th>
                    <th scope="col" className={thClass}>Came from</th>
                    <th scope="col" className={thClass}>Batch</th>
                    <th scope="col" className={thClass}>Went to</th>
                    <th scope="col" className={thClass}>Status</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {isError ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The serial numbers could not be loaded.')}</td></tr>
                  ) : isLoading ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center">
                        <p className="text-sm font-medium text-gray-900">{search || status ? 'No unit matches' : 'No serial numbers yet'}</p>
                        <p className="mt-1 text-sm text-gray-500">Switch serial numbers on for a product; its units are then numbered as they are made.</p>
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-3"><button type="button" onClick={() => setViewingId(row.id)} className="font-mono text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.serialNumber}</button></td>
                        <td className="px-4 py-3 text-sm text-gray-900">{row.productName}<span className="block text-xs text-gray-500">{row.sku}</span></td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{origin(row)}<span className="block text-xs text-gray-500">{SOURCE[row.source]} · {formatDate(row.createdAt)}</span></td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.batchNumber || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{row.customerName ? <>{row.customerName}<span className="block text-xs text-gray-500">{row.challanNumber} · {formatDate(row.dispatchedOn)}</span></> : '—'}</td>
                        <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                        <td className="whitespace-nowrap px-4 py-3 text-right"><button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.serialNumber}`}>Open</button></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
                <label className="flex items-center gap-2">
                  Rows per page
                  <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
                  </select>
                </label>
              </div>
              {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
            </div>
          </>
        )}
      </div>

      <Modal isOpen={registerFor !== null || !!viewingId} onClose={() => (registerFor !== null ? setRegisterFor(null) : setViewingId(null))} title={registerFor !== null ? 'Register Units' : viewing?.serialNumber ?? 'Serial Number'} size="xl">
        {registerFor !== null ? (
          <RegisterForm key={registerFor} products={products} initialProductId={registerFor} isPending={register.isPending} onCancel={() => setRegisterFor(null)} onSubmit={(input) => register.mutate(input)} />
        ) : (
          viewingId && <SerialPanel key={viewingId} serialId={viewingId} canEdit={canEdit} canDelete={canDelete} onClose={() => setViewingId(null)} onChanged={refresh} />
        )}
      </Modal>
    </div>
  );
};

export default Serials;
